import { useQuota } from 'features/quota/hooks/useQuota';

import type { TooltipProps } from '../../../components/Tooltip';
import { TooltipWithContactUs } from '../../../components/TooltipWithContactUs';
import { CONTACTS_FEATURE_KEY } from '../../../domain/gainAccessView';
import { CONTACTS_FULL_ACCESS, CONTACTS_LIMIT_INCREASE } from '../../../domain/user';
import { useUserStatus } from '../../../hooks/useUserStatus';
import { translate } from '../../../services/i18n';
import { useDispatchTrackEventWithLocation } from '../../../services/tracking/hooks/useDispatchTrackEventWithLocation';

export const ContactsQuotaTooltip = ({
  children,
  location,
  ...rest
}: {
  children: React.ReactNode;
  location: 'top counter' | 'reveal button';
} & Omit<TooltipProps, 'content'>) => {
  const trackEvent = useDispatchTrackEventWithLocation();
  const { isTrial, isAfterTrial, isTrialOrAfterTrial } = useUserStatus();

  const {
    quota: { total, used },
  } = useQuota();

  const handleContactUsClick = () => {
    const action = isTrialOrAfterTrial ? 'upgrade package' : 'contact us';

    trackEvent('hooks', action, `get contact credits/${location}/${total}`);
  };

  const getTooltipKey = () => {
    if (isTrial) {
      return `header.credits.contacts.limit_reached.tooltip.trial`;
    }
    if (isAfterTrial) {
      return `header.credits.contacts.limit_reached.tooltip.after-trial`;
    }
    return used > 0 ? `contact_card.no_credits` : `header.credits.contacts.limit_reached.tooltip.paid`;
  };

  return (
    <TooltipWithContactUs
      {...rest}
      featureKey={CONTACTS_FEATURE_KEY}
      settingKey={isTrialOrAfterTrial ? CONTACTS_FULL_ACCESS : CONTACTS_LIMIT_INCREASE}
      text={translate(getTooltipKey())}
      accessRequestedText={`${translate(getTooltipKey())} ${translate('hook.contact_us.title')}.`}
      onContactUsClick={handleContactUsClick}
    >
      {children}
    </TooltipWithContactUs>
  );
};
